import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
  useMemo
} from 'react'
import classNames from 'classnames'
import styles from './styles.module.scss'
import IconCalendar from '/public/icons/icon-calendar.svg'
import CustomField from '../../../form/CustomField'
import { useResponsive } from '../../../../../providers/ResponsiveContext'
import { useSearchContext } from '../../../../../providers/SearchContext'
import { useRouter } from 'next/router'
import moment from 'moment'
import dynamic from 'next/dynamic'
import { createPortal } from 'react-dom'
import { preloadReactDates } from '../../../form/Calendar'
import { useDateSync } from './hooks/useDateSync'

// Dynamic import for Calendar to support SSR
const Calendar = dynamic(() => import('../../../form/Calendar'), {
  ssr: false,
  loading: () => <div>Loading calendar...</div>,
  onError: (error) => {
    if (
      typeof process !== 'undefined' &&
      // eslint-disable-next-line no-undef
      process.env.NODE_ENV === 'development'
    ) {
      console.error('[DatesRangePicker] Failed to load Calendar:', error)
    }
    // Return fallback component
    return () => (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Unable to load calendar. Please refresh the page.
      </div>
    )
  }
})

// Preload Calendar component on hover
const preloadCalendar = () => {
  Calendar.preload?.()
  preloadReactDates()
}

// Dropdown portal positioning constants
const DROPDOWN_MIN_WIDTH_PX = 600 // Two-month calendar minimum width
const VIEWPORT_EDGE_PADDING_PX = 16 // Breathing room from screen edges

// Translations for flexibility buttons and date display
const flexibilityTranslations = {
  exactDates: {
    en: 'Exact Dates',
    fr: 'Dates exactes',
    es: 'Fechas exactas',
    nl: 'Exacte data'
  },
  plusMinusOneDay: {
    en: '\u00b1 1 day',
    fr: '\u00b1 1 jour',
    es: '\u00b1 1 d\u00eda',
    nl: '\u00b1 1 dag'
  },
  plusMinusTwoDays: {
    en: '\u00b1 2 days',
    fr: '\u00b1 2 jours',
    es: '\u00b1 2 d\u00edas',
    nl: '\u00b1 2 dagen'
  },
  plusMinusThreeDays: {
    en: '\u00b1 3 days',
    fr: '\u00b1 3 jours',
    es: '\u00b1 3 d\u00edas',
    nl: '\u00b1 3 dagen'
  },
  clearDates: {
    en: 'Clear dates',
    fr: 'Effacer les dates',
    es: 'Borrar fechas',
    nl: 'Datums wissen'
  },
  selectDates: {
    en: 'Select dates',
    fr: 'S\u00e9lectionner les dates',
    es: 'Seleccionar fechas',
    nl: 'Selecteer datums'
  },
  day: {
    en: 'day',
    fr: 'jour',
    es: 'd\u00eda',
    nl: 'dag'
  },
  days: {
    en: 'days',
    fr: 'jours',
    es: 'd\u00edas',
    nl: 'dagen'
  }
}

/**
 * DatesRangePicker - Unified date range picker component
 *
 * @param {Object} form - Formik form object with setFieldValue and values
 * @param {Object} shadowRef - Ref for shadow overlay (homepage)
 * @param {Function} setCalendarActive - Callback to set calendar active state (homepage)
 * @param {boolean} custom - Custom positioning variant
 * @param {boolean} icon - Show calendar icon in input (default: false for CustomField, true for property variant)
 * @param {Object} translations - External translations (falls back to internal translations)
 * @param {number} width - Input width (for CustomField variant)
 * @param {string} variant - Visual variant: "homepage", "filter", "property" (default: "homepage")
 * @param {boolean} showFlexibilityButtons - Show flexibility buttons (default: true)
 * @param {Array} blockedDates - Array of moment dates that are blocked (property pages)
 * @param {number} minimumNights - Minimum nights required (default: 1)
 * @param {Object} property - Property object for property variant (contains is_active, terms.minNights)
 * @param {Function} submitForm - Submit form callback (for filter variant on mobile)
 * @param {number} dropdownWidth - Custom dropdown width in pixels (property variant)
 * @param {Array} fieldVariant - Variant array to pass to CustomField (e.g., ['dark', 'dropdown'])
 */
const DatesRangePicker = memo(
  ({
    form: { setFieldValue, values },
    shadowRef,
    setCalendarActive,
    custom,
    icon = false,
    translations,
    width,
    variant = 'homepage',
    showFlexibilityButtons = true,
    blockedDates = null,
    minimumNights = 1,
    property = null,
    submitForm,
    dropdownWidth,
    fieldVariant
  }) => {
    const {
      search,
      reset,
      initialValues,
      setOffsetable,
      setSearch,
      offsetable
    } = useSearchContext()
    const router = useRouter()

    // Determine variant types (must be declared before use)
    const isPropertyVariant = variant === 'property'
    const isFilterVariant = variant === 'filter'

    // For filter variant on mobile, show calendar expanded by default
    const { isMobile, isSmallMobile, isLargeDesktop } = useResponsive()
    const shouldAutoExpand = isFilterVariant && !isLargeDesktop
    const [showCalendar, setShowCalendar] = useState(shouldAutoExpand)
    const wrapperRef = useRef(null)
    // For property variant, only load dates if offsetable is 0 (exact dates)
    // For other variants, always load dates from search context
    // Note: Initial state uses search context, but sync effect will update if dates load asynchronously
    const shouldLoadDates = !isPropertyVariant || offsetable === 0
    const [startDate, setStartDate] = useState(() => {
      return shouldLoadDates ? search?.startDate || null : null
    })
    const [endDate, setEndDate] = useState(() => {
      return shouldLoadDates ? search?.endDate || null : null
    })
    const [loaded, setLoaded] = useState(false)
    const [flexibility, setFlexibility] = useState(0) // 0 = exact, 1, 2, or 3 days
    const [portalContainer, setPortalContainer] = useState(null)
    const [dropdownPosition, setDropdownPosition] = useState(null)
    const dropdownPortalRef = useRef({ node: null })
    const calendarDropdownRef = useRef(null)
    const hasSyncedInitialDatesRef = useRef(false)
    const searchContextDatesRef = useRef({ startDate: null, endDate: null })

    // Use portal for filter variant on large desktop, homepage variant on non-mobile, or property variant on non-mobile
    // This ensures the calendar dropdown can overflow parent containers (like grids) that may clip content
    // On mobile, don't use portal so CSS mobile styles can properly constrain the width
    const shouldUsePortal =
      (isFilterVariant && isLargeDesktop) ||
      (variant === 'homepage' && !isMobile) ||
      (isPropertyVariant && !isMobile)

    // Get minimum nights from property or prop
    const effectiveMinimumNights = property?.terms?.minNights || minimumNights

    // Get translations - use provided translations or fallback to internal
    const getTranslation = useCallback(
      (key) => {
        // Try external translations first (for homepage/filter variants)
        if (translations?.dates) {
          if (key === 'dates') return translations.dates[router.locale]
          if (key === 'clear' && translations.clear)
            return translations.clear[router.locale]
          if (key === 'exactDates' && translations.exactDates)
            return translations.exactDates[router.locale]
          if (key === 'days' && translations.days)
            return translations.days[router.locale]
        }
        // Fallback to internal translations
        if (key === 'dates')
          return flexibilityTranslations.selectDates[router.locale]
        if (key === 'clear')
          return flexibilityTranslations.clearDates[router.locale]
        if (key === 'exactDates')
          return flexibilityTranslations.exactDates[router.locale]
        if (key === 'days') return flexibilityTranslations.days[router.locale]
        return flexibilityTranslations[key]?.[router.locale] || key
      },
      [translations, router.locale]
    )

    // Memoize formatted date strings to avoid recalculation
    const formattedDates = useMemo(() => {
      if (!startDate) return getTranslation('dates')

      const startMonth = moment(startDate).format('MMM').toUpperCase()
      const startDay = moment(startDate).format('D')

      if (!endDate) {
        let dateString = `${startMonth} ${startDay}`
        // Add flexibility indicator if enabled and not exact dates
        if (showFlexibilityButtons && flexibility > 0) {
          const dayText =
            flexibility === 1
              ? flexibilityTranslations.day[router.locale]
              : flexibilityTranslations.days[router.locale]
          dateString += ` (\u00b1${flexibility} ${dayText})`
        }
        return dateString
      }

      const endMonth = moment(endDate).format('MMM').toUpperCase()
      const endDay = moment(endDate).format('D')

      // Smart month display - only show month once if same month
      let dateString
      if (startMonth === endMonth) {
        dateString = `${startMonth} ${startDay}-${endDay}`
      } else {
        dateString = `${startMonth} ${startDay} - ${endMonth} ${endDay}`
      }

      // Add flexibility indicator if enabled and not exact dates
      if (showFlexibilityButtons && flexibility > 0) {
        const dayText =
          flexibility === 1
            ? flexibilityTranslations.day[router.locale]
            : flexibilityTranslations.days[router.locale]
        dateString += ` (\u00b1${flexibility} ${dayText})`
      }

      return dateString
    }, [
      startDate,
      endDate,
      flexibility,
      showFlexibilityButtons,
      getTranslation,
      router.locale
    ])

    // Handle date changes from Calendar
    const handleStartDateChange = useCallback(
      (newStartDate) => {
        const normalizedStartDate = newStartDate
          ? moment(newStartDate).utc().startOf('day')
          : null

        // Auto-clear checkout if new check-in is after current checkout
        if (
          normalizedStartDate &&
          endDate &&
          normalizedStartDate.isAfter(endDate)
        ) {
          setStartDate(normalizedStartDate)
          setEndDate(null)
        } else {
          setStartDate(normalizedStartDate)
        }
      },
      [endDate]
    )

    const handleEndDateChange = useCallback((newEndDate) => {
      const normalizedEndDate = newEndDate
        ? moment(newEndDate).utc().startOf('day')
        : null
      setEndDate(normalizedEndDate)
    }, [])

    // Handle click outside
    const handleClickOutside = useCallback(
      (event) => {
        // Don't close calendar in mobile modal for filter variant
        if (shouldAutoExpand) {
          return
        }

        // Check if click is inside wrapper (input field)
        const clickedInsideWrapper = wrapperRef.current?.contains(event.target)

        // Check if click is inside calendar dropdown (both portal and non-portal)
        const clickedInsideDropdown = calendarDropdownRef.current?.contains(
          event.target
        )

        // Check if click is inside portal container (when using portal)
        const clickedInsidePortal = portalContainer?.contains(event.target)

        // Check if click is on calendar elements (using data attributes for reliability)
        const clickedOnCalendarElements =
          event.target.closest('[data-calendar-element]') ||
          event.target.closest('[class*="DayPicker"]') ||
          event.target.closest('.calendar-footer')

        // Only close if click is outside all calendar-related elements
        if (
          !clickedInsideWrapper &&
          !clickedInsideDropdown &&
          !clickedInsidePortal &&
          !clickedOnCalendarElements
        ) {
          setShowCalendar(false)
        }
      },
      [shouldAutoExpand, portalContainer]
    )

    // Set up click outside handler
    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [handleClickOutside])

    // Sync dates between search context and local state
    // Extracted to custom hook for maintainability
    useDateSync({
      searchStartDate: search.startDate,
      searchEndDate: search.endDate,
      localStartDate: startDate,
      localEndDate: endDate,
      setLocalStartDate: setStartDate,
      setLocalEndDate: setEndDate,
      setFieldValue,
      isPropertyVariant,
      offsetable,
      showCalendar,
      isFilterVariant,
      routerPathname: router.pathname,
      hasSyncedInitialDatesRef,
      searchContextDatesRef
    })

    // Update form values and search context when dates change
    useEffect(() => {
      setFieldValue('startDate', startDate)
      setFieldValue('endDate', endDate)

      // Update search context for all variants to ensure dates persist
      // Only update if dates actually changed to avoid unnecessary updates
      if (setSearch) {
        setSearch((prevState) => {
          const prevStartValue = prevState.startDate?.valueOf()
          const prevEndValue = prevState.endDate?.valueOf()
          const newStartValue = startDate?.valueOf()
          const newEndValue = endDate?.valueOf()

          // Only update if dates actually changed
          if (
            prevStartValue !== newStartValue ||
            prevEndValue !== newEndValue
          ) {
            return {
              ...prevState,
              startDate: startDate,
              endDate: endDate
            }
          }
          return prevState
        })
      }
    }, [startDate, endDate, setFieldValue, setSearch])

    // Update offsetable in search context when flexibility changes (only if enabled)
    useEffect(() => {
      if (showFlexibilityButtons) {
        setOffsetable(flexibility)
      } else {
        // Property variant always sets offsetable to 0
        setOffsetable(0)
      }
    }, [flexibility, setOffsetable, showFlexibilityButtons])

    // Handle shadow overlay (homepage variant)
    useEffect(() => {
      if (shadowRef) {
        if (showCalendar) {
          shadowRef.current?.classList.add('isVisible')
        } else {
          shadowRef.current?.classList.remove('isVisible')
        }
      }
    }, [shadowRef, showCalendar])

    // Handle reset from search context
    useEffect(() => {
      const shouldReset = reset || search?.reset
      if (shouldReset) {
        setStartDate(initialValues?.startDate)
        setEndDate(initialValues?.endDate)
        setFlexibility(0)
      }
    }, [reset, search?.reset, initialValues])

    // Update parent component's active state (homepage variant)
    useEffect(() => {
      if (setCalendarActive) {
        setCalendarActive((search.startDate && search.endDate) || showCalendar)
      }
    }, [
      showCalendar,
      search.startDate,
      search.endDate,
      setCalendarActive,
      values
    ])

    // Set loaded state after mount
    useEffect(() => setLoaded(true), [])

    useEffect(() => {
      if (typeof document === 'undefined') return
      const node = document.createElement('div')
      node.setAttribute('data-dates-portal', 'true')
      document.body.appendChild(node)
      setPortalContainer(node)
      dropdownPortalRef.current.node = node
      return () => {
        // Defensive cleanup: only remove if node is still in DOM
        if (node.parentNode) {
          node.parentNode.removeChild(node)
        }
        dropdownPortalRef.current.node = null
      }
    }, [])

    const updateDropdownPosition = useCallback(() => {
      if (
        !shouldUsePortal ||
        !wrapperRef.current ||
        typeof window === 'undefined'
      ) {
        return
      }

      const rect = wrapperRef.current.getBoundingClientRect()
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || 0
      const minWidth = dropdownWidth || DROPDOWN_MIN_WIDTH_PX
      const width = Math.max(rect.width, minWidth)
      const maxLeft = Math.max(
        VIEWPORT_EDGE_PADDING_PX,
        viewportWidth - width - VIEWPORT_EDGE_PADDING_PX
      )
      const left = Math.min(
        Math.max(rect.left, VIEWPORT_EDGE_PADDING_PX),
        Math.max(maxLeft, VIEWPORT_EDGE_PADDING_PX)
      )

      setDropdownPosition({
        top: rect.bottom + 8,
        left,
        width
      })
    }, [shouldUsePortal, dropdownWidth])

    useEffect(() => {
      if (!shouldUsePortal || !showCalendar) {
        return
      }

      updateDropdownPosition()

      // Throttle scroll handler to prevent excessive re-renders
      // Uses 100ms delay to balance responsiveness with performance
      let scrollThrottleTimer = null
      const handleReposition = () => {
        updateDropdownPosition()
      }

      const throttledScrollHandler = () => {
        if (scrollThrottleTimer === null) {
          scrollThrottleTimer = setTimeout(() => {
            handleReposition()
            scrollThrottleTimer = null
          }, 100)
        }
      }

      window.addEventListener('resize', handleReposition)
      window.addEventListener('scroll', throttledScrollHandler, true)

      return () => {
        window.removeEventListener('resize', handleReposition)
        window.removeEventListener('scroll', throttledScrollHandler, true)
        // Clear any pending throttle timer on cleanup
        if (scrollThrottleTimer !== null) {
          clearTimeout(scrollThrottleTimer)
        }
      }
    }, [shouldUsePortal, showCalendar, updateDropdownPosition])

    useEffect(() => {
      if (!shouldUsePortal) {
        setDropdownPosition(null)
      }
    }, [shouldUsePortal])

    // Keep calendar expanded in mobile modal for filter variant
    useEffect(() => {
      if (shouldAutoExpand) {
        setShowCalendar(true)
      }
    }, [shouldAutoExpand])

    // Handle toggle calendar
    const toggleCalendar = useCallback(() => {
      // Don't allow closing in mobile modal for filter variant
      if (shouldAutoExpand) {
        return
      }
      setShowCalendar((prev) => !prev)
    }, [shouldAutoExpand])

    // Handle clear dates
    const handleClearDates = useCallback(() => {
      setStartDate(null)
      setEndDate(null)
      setFlexibility(0)
      setFieldValue('startDate', null)
      setFieldValue('endDate', null)
      setOffsetable(0)

      // Update search context for all variants to ensure dates are cleared
      if (setSearch) {
        setSearch((prevState) => ({
          ...prevState,
          startDate: null,
          endDate: null
        }))
      }

      // Auto-submit on mobile for filter variant
      if (isFilterVariant && !isLargeDesktop && submitForm) {
        setTimeout(async () => {
          await submitForm()
        }, 50)
      }
    }, [
      setFieldValue,
      setOffsetable,
      isFilterVariant,
      isLargeDesktop,
      submitForm,
      setSearch
    ])

    // Handle flexibility button click
    const handleFlexibilityClick = useCallback((days) => {
      setFlexibility(days)
    }, [])

    // Date range display
    const dateRangeString = formattedDates

    // Determine number of months based on screen size
    const numberOfMonths = isMobile || isSmallMobile ? 1 : 2

    // Check if property is active (for property variant)
    const isDisabled = isPropertyVariant && property && !property.is_active

    // Calendar translations object
    const calendarTranslations = translations || {
      dates: flexibilityTranslations.selectDates,
      clear: { en: 'Clear', fr: 'Effacer', es: 'Borrar', nl: 'Wissen' }
    }

    // Render input based on variant
    const renderInput = () => {
      if (isPropertyVariant) {
        // Property variant uses custom button with icon
        const isActive = startDate || endDate || showCalendar
        return (
          <button
            type="button"
            className={classNames(
              styles.dates__input,
              isActive && styles['dates__input--active']
            )}
            onClick={toggleCalendar}
            onMouseEnter={preloadCalendar}
            aria-label="Select dates"
            disabled={isDisabled}
            title={
              isDisabled ? 'This property is currently unavailable' : undefined
            }
          >
            <IconCalendar
              width={22}
              height={22}
              className={styles.dates__icon}
            />
            <span className={styles.dates__value}>{dateRangeString}</span>
          </button>
        )
      }

      // Homepage and filter variants use CustomField
      // Compute variant: use fieldVariant if provided, otherwise default based on mobile state
      const computedVariant = fieldVariant || [isMobile && 'full-width']

      return (
        <CustomField
          aria-label="Select dates"
          type="text"
          readOnly
          angle
          width={width || (isFilterVariant && !isMobile ? 13 : undefined)}
          variant={computedVariant}
          prependIcon={
            icon ? <IconCalendar width={26} height={26} /> : undefined
          }
          onClick={toggleCalendar}
          onMouseEnter={preloadCalendar}
          isActive={(startDate && endDate) || showCalendar}
          value={dateRangeString}
          disabled={isDisabled}
          title={
            isDisabled ? 'This property is currently unavailable' : undefined
          }
        />
      )
    }

    // Render flexibility buttons
    const renderFlexibilityButtons = () => {
      if (!showFlexibilityButtons) return null

      return (
        <div className={styles.dates__flexibility}>
          <button
            type="button"
            aria-pressed={flexibility === 0}
            className={classNames(
              styles.dates__flexButton,
              flexibility === 0 && styles['dates__flexButton--active']
            )}
            onClick={() => handleFlexibilityClick(0)}
          >
            {getTranslation('exactDates')}
          </button>
          <button
            type="button"
            aria-pressed={flexibility === 1}
            className={classNames(
              styles.dates__flexButton,
              flexibility === 1 && styles['dates__flexButton--active']
            )}
            onClick={() => handleFlexibilityClick(1)}
          >
            {flexibilityTranslations.plusMinusOneDay[router.locale]}
          </button>
          <button
            type="button"
            aria-pressed={flexibility === 2}
            className={classNames(
              styles.dates__flexButton,
              flexibility === 2 && styles['dates__flexButton--active']
            )}
            onClick={() => handleFlexibilityClick(2)}
          >
            {flexibilityTranslations.plusMinusTwoDays[router.locale]}
          </button>
          <button
            type="button"
            aria-pressed={flexibility === 3}
            className={classNames(
              styles.dates__flexButton,
              flexibility === 3 && styles['dates__flexButton--active']
            )}
            onClick={() => handleFlexibilityClick(3)}
          >
            {flexibilityTranslations.plusMinusThreeDays[router.locale]}
          </button>
        </div>
      )
    }

    // Render clear button
    const renderClearButton = () => {
      const clearText =
        isFilterVariant && translations?.clear
          ? `${translations.clear[router.locale]} ${translations.dates[
              router.locale
            ].toLowerCase()}`
          : getTranslation('clear')

      return (
        <button
          type="button"
          className={styles.dates__clearButton}
          onClick={handleClearDates}
        >
          {clearText}
        </button>
      )
    }

    const dropdownClassName = classNames(
      styles.dates__wrapper,
      showCalendar && styles.isActive,
      custom && styles.custom,
      isPropertyVariant && styles['dates__wrapper--property'],
      isFilterVariant && styles['dates__wrapper--filter']
    )

    const dropdownStyle = useMemo(() => {
      const baseStyle = dropdownWidth
        ? { '--dropdown-width': `${dropdownWidth}px` }
        : undefined

      if (shouldUsePortal && dropdownPosition) {
        return {
          ...baseStyle,
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 10000
        }
      }

      return baseStyle
    }, [dropdownWidth, shouldUsePortal, dropdownPosition])

    const calendarContent = (
      <div
        ref={calendarDropdownRef}
        className={dropdownClassName}
        style={dropdownStyle}
        data-calendar-element="true"
        data-calendar-debug={
          shouldUsePortal
            ? JSON.stringify({
                rendered: loaded && showCalendar,
                width: dropdownPosition?.width || null,
                height: dropdownPosition
                  ? dropdownPosition?.height || null
                  : null
              })
            : undefined
        }
      >
        {loaded && showCalendar && (
          <Calendar
            variant={['large']}
            startDate={startDate}
            endDate={endDate}
            setStartDate={handleStartDateChange}
            setEndDate={handleEndDateChange}
            blockedDates={blockedDates}
            minimumNights={effectiveMinimumNights}
            translations={calendarTranslations}
            numberOfMonths={numberOfMonths}
          >
            <div
              className={classNames(
                styles.dates__footer,
                !showFlexibilityButtons && styles['dates__footer--no-flex']
              )}
            >
              {renderFlexibilityButtons()}
              {renderClearButton()}
            </div>
          </Calendar>
        )}
      </div>
    )

    return (
      <div
        ref={wrapperRef}
        className={classNames(
          styles.dates,
          isMobile && styles['dates--mobile'],
          isPropertyVariant && styles['dates--property'],
          isFilterVariant && styles['dates--filter']
        )}
        onMouseEnter={preloadCalendar}
      >
        {renderInput()}

        {shouldUsePortal && portalContainer
          ? createPortal(calendarContent, portalContainer)
          : calendarContent}
      </div>
    )
  }
)

// Display name for debugging
DatesRangePicker.displayName = 'DatesRangePicker'

export default DatesRangePicker
